/**
 * SimulatedAIPipelineDemo — a reusable, marketing-grade animation that
 * mimics JTR's real-time AI monitoring panel using generic fake data.
 * Designed to be dropped on the home page, Why-JTR, How-It-Works,
 * Personal Overview, and Business Overview so visitors can SEE what
 * JTR does instead of just reading about it.
 *
 * Props:
 *   variant: 'personal' | 'business'  (controls labels, fake company / card)
 *   compact: boolean — reduce vertical footprint (skip log stream)
 *
 * Behaviour:
 *   - Cycles continuously through 6 stages (Pull → Parse → Validate →
 *     Anomaly check → AI guard → Deliver). Each stage takes ~2-3s.
 *   - Active stage pulses, completed stages get a green check.
 *   - Generates rolling fake log lines for the bottom console.
 *   - On completion, waits 2s then restarts.
 *   - 100% client-side, no network. Pauses when off-screen via
 *     IntersectionObserver to save CPU.
 */
import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/i18n';
import {
  Database, FileText, ShieldCheck, AlertTriangle, Brain, Send,
  CheckCircle2, Cpu, Activity, Loader2,
} from 'lucide-react';

const STAGES = [
  { id: 'pull',     Icon: Database,     dur: 2200, color: '#0083D3',
    ja: 'ETC利用照会サービス（MEISAI）からデータ取得', en: 'Pulling from MEISAI' },
  { id: 'parse',    Icon: FileText,     dur: 1800, color: '#9200C7',
    ja: '利用明細の解析',        en: 'Parsing toll records' },
  { id: 'validate', Icon: ShieldCheck,  dur: 2000, color: '#62BB46',
    ja: '31項目の検証',     en: '31 deterministic checks' },
  { id: 'anomaly',  Icon: AlertTriangle,dur: 1700, color: '#F59E0B',
    ja: '異常値スキャン',         en: 'Anomaly scan' },
  { id: 'ai',       Icon: Brain,        dur: 2400, color: '#0F766E',
    ja: '品質確認',              en: 'Accuracy gate' },
  { id: 'deliver',  Icon: Send,         dur: 1500, color: '#10B981',
    ja: 'メール配信',              en: 'Email delivery' },
];

const RESET_DELAY = 2200;

// Fake log lines — drawn from a pool, randomized per cycle
function buildLogPool(ja, isBiz) {
  const company = isBiz ? 'ACME Logistics K.K.' : 'Tanaka, H.';
  const card = isBiz ? 'ETC-****-1247 (+ 49)' : 'ETC-****-3018';
  return [
    ja ? `▸ ETC利用照会サービス（MEISAI）接続：${card}`              : `▸ MEISAI connect: ${card}`,
    ja ? `▸ 認証成功・Cookie確立（200）`       : `▸ Auth OK · cookie established (200)`,
    ja ? `▸ 利用明細：42件取得（2.1秒）`         : `▸ Toll records fetched: 42 rows (2.1s)`,
    ja ? `▸ 重複検出：0件・整合性 OK`         : `▸ Duplicate scan: 0 hits · integrity OK`,
    ja ? `▸ プレート照合：一致 ✓`              : `▸ Plate match: confirmed ✓`,
    ja ? `▸ 異常値：該当なし`                 : `▸ Anomaly scan: none flagged`,
    ja ? `▸ 品質確認：信頼度 0.987・PASS`   : `▸ Accuracy gate: confidence 0.987 · PASS`,
    ja ? `▸ CSV出力・42行・4.1 KB`       : `▸ CSV emitted · 42 rows · 4.1 KB`,
    ja ? `▸ PDF生成完了・3ページ`         : `▸ PDF rendered · 3 pages`,
    ja ? `▸ HTML本文：日英両対応`            : `▸ HTML body: JA + EN`,
    ja ? `▸ 配信前チェック ✓・送信待ち`      : `▸ Delivery gate ✓ · queued`,
    ja ? `▸ Mandrill：送信完了（ID:msg_a8c1）`  : `▸ Mandrill: delivered (id:msg_a8c1)`,
    ja ? `▸ ${company}・経理担当者に通知済み`     : `▸ ${company} · accounting notified`,
  ];
}

export default function SimulatedAIPipelineDemo({ variant = 'personal', compact = false }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const isBiz = variant === 'business';

  const [stageIdx, setStageIdx] = useState(0);
  const [doneIdx, setDoneIdx] = useState(-1); // last completed stage
  const [logs, setLogs] = useState([]);
  const [paused, setPaused] = useState(false);

  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const logTimerRef = useRef(null);

  // Pause when off-screen
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => setPaused(!e.isIntersecting)); },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Stage advancement
  useEffect(() => {
    if (paused) return;
    const stage = STAGES[stageIdx];
    if (!stage) return;
    timerRef.current = setTimeout(() => {
      setDoneIdx(stageIdx);
      if (stageIdx + 1 < STAGES.length) {
        setStageIdx(stageIdx + 1);
      } else {
        // Cycle done — reset
        setTimeout(() => { setStageIdx(0); setDoneIdx(-1); setLogs([]); }, RESET_DELAY);
      }
    }, stage.dur);
    return () => clearTimeout(timerRef.current);
  }, [stageIdx, paused]);

  // Log stream
  useEffect(() => {
    if (paused || compact) return;
    const pool = buildLogPool(ja, isBiz);
    let idx = 0;
    logTimerRef.current = setInterval(() => {
      setLogs((prev) => {
        const next = [...prev, { id: Date.now() + idx, line: pool[idx % pool.length], ts: new Date() }];
        return next.slice(-8); // keep last 8
      });
      idx += 1;
    }, 850);
    return () => clearInterval(logTimerRef.current);
  }, [paused, ja, isBiz, compact, stageIdx]);

  const progressPct = ((doneIdx + 1) / STAGES.length) * 100;
  const headline = isBiz ? (ja ? 'ACME Logistics K.K.・50枚・月次レポート'
                                : 'ACME Logistics K.K. · 50 cards · Monthly report')
                         : (ja ? '田中H.様・ETCカード #3018・月次レポート'
                                : 'Tanaka, H. · ETC card #3018 · Monthly report');

  return (
    <div
      ref={containerRef}
      className="relative rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 shadow-2xl overflow-hidden"
      data-testid={`ai-pipeline-demo-${variant}`}
    >
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '24px 24px' }} aria-hidden="true" />
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-purple-500/15 blur-3xl" aria-hidden="true" />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-3 px-5 py-3.5 border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex gap-1.5 flex-shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
          </div>
          <Cpu className="w-3.5 h-3.5 text-emerald-400 ml-1 flex-shrink-0" strokeWidth={2.2} />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 whitespace-nowrap">
            {ja ? 'JTR AI処理・リアルタイム' : 'JTR AI Pipeline · Live'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[10px] font-bold text-white/60 tracking-wider hidden sm:inline">
            {ja ? 'デモ・自動再生' : 'DEMO · Auto-play'}
          </span>
        </div>
      </div>

      {/* Job line + progress */}
      <div className="relative px-5 pt-4 pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" strokeWidth={2.4} />
            <span className="text-xs font-bold text-white/90 truncate">{headline}</span>
          </div>
          <span className="text-[10px] font-mono text-emerald-300">{Math.round(progressPct)}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-400 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Stages */}
      <div className="relative px-5 py-4">
        <div className="space-y-1.5">
          {STAGES.map((s, i) => {
            const Icon = s.Icon;
            const isActive = i === stageIdx;
            const isDone = i <= doneIdx;
            return (
              <div
                key={s.id}
                className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300 ${
                  isActive ? 'bg-white/10 border border-white/20'
                          : isDone ? 'bg-emerald-500/10 border border-emerald-500/20'
                                   : 'bg-transparent border border-transparent opacity-60'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    isActive ? 'scale-110' : ''
                  }`}
                  style={{ backgroundColor: isDone || isActive ? s.color : 'rgba(255,255,255,0.06)' }}
                >
                  {isDone && !isActive ? (
                    <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.8} />
                  ) : isActive ? (
                    <Icon className="w-4 h-4 text-white animate-pulse" strokeWidth={2.4} />
                  ) : (
                    <Icon className="w-4 h-4 text-white/50" strokeWidth={2} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-white/90 truncate">{ja ? s.ja : s.en}</div>
                </div>
                {isActive && (
                  <Loader2 className="w-3.5 h-3.5 text-emerald-300 animate-spin flex-shrink-0" strokeWidth={2.4} />
                )}
                {isDone && !isActive && (
                  <span className="text-[10px] font-mono text-emerald-300 flex-shrink-0">OK</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Log stream */}
      {!compact && (
        <div className="relative border-t border-white/10 bg-black/40 backdrop-blur-sm px-5 py-3 min-h-[120px] max-h-[140px] overflow-hidden">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-400/70 mb-1">
            {ja ? 'ログ出力' : 'Log stream'}
          </div>
          <div className="space-y-0.5 font-mono text-[10px] leading-[1.4]">
            {logs.length === 0 && (
              <div className="text-white/40 italic">{ja ? '初期化中…' : 'Initializing…'}</div>
            )}
            {logs.map((l, i) => (
              <div
                key={l.id}
                className={`text-emerald-300/80 transition-opacity duration-500 ${i === logs.length - 1 ? 'opacity-100' : 'opacity-50'}`}
              >
                <span className="text-white/30">[{l.ts.toLocaleTimeString(ja ? 'ja-JP' : 'en-US', { hour12: false })}]</span>{' '}
                {l.line}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer caption */}
      <div className="relative border-t border-white/10 bg-black/30 px-5 py-2 text-[9px] text-white/50 leading-relaxed">
        {ja
          ? '※ デモ表示です。実際のお客様データは表示されません。'
          : '※ Demo visualization. No real customer data is shown.'}
      </div>
    </div>
  );
}
